import frontMatter from 'front-matter';
import { marked } from 'marked';

// 处理本地媒体文件路径
function processMediaPath(filePath: string): string {
  // 统一使用正斜杠
  const normalizedPath = filePath.replace(/\\/g, '/');
  
  if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
    // 如果是完整的 URL，保持不变
    return normalizedPath;
  }

  if (normalizedPath.startsWith('/')) {
    // 如果是绝对路径，保持不变
    return normalizedPath;
  }

  // 如果是相对路径，添加 /blog_content 前缀
  return `/blog_content/${normalizedPath}`;
}

// 配置 marked 选项
marked.setOptions({
  gfm: true,
  breaks: true
});

// 处理 HTML 内容中的图片和链接
function processHtmlContent(html: string): string {
  // 处理图片
  html = html.replace(/<img[^>]+src="([^"]+)"[^>]*>/g, (match, src) => {
    const processedSrc = processMediaPath(src);
    return match.replace(src, processedSrc);
  });

  // 处理链接
  html = html.replace(/<a[^>]+href="([^"]+)"[^>]*>/g, (match, href) => {
    const processedHref = processMediaPath(href);
    return match.replace(href, processedHref);
  });

  // 处理视频
  html = html.replace(/<video[^>]+src="([^"]+)"[^>]*>/g, (match, src) => {
    const processedSrc = processMediaPath(src);
    return match.replace(src, processedSrc);
  });

  return html;
}

// 使用 Vite 的 import.meta.glob 来预加载所有 MD 文件
const posts = import.meta.glob('/blog_content/posts/*.md', { 
  eager: true, 
  query: '?raw',
  import: 'default'
});

interface PostAttributes {
  title?: string;
  date?: string;
  author?: string;
  tags?: string[];
  image?: string;
  video?: {
    url: string;
    type: 'youtube' | 'vimeo' | 'local';
    thumbnail?: string;
  };
  gallery?: {
    images: string[];
    layout: 'grid' | 'carousel';
  };
}

export interface Post {
  slug: string;
  title: string;
  date: string;
  author: string;
  excerpt: string;
  content: string;
  htmlContent: string;
  tags: string[];
  image?: string;
  video?: {
    url: string;
    type: 'youtube' | 'vimeo' | 'local';
    thumbnail?: string;
  };
  gallery?: {
    images: string[];
    layout: 'grid' | 'carousel';
  };
}

// 默认图片配置
const defaultImages: Record<string, string> = {
  'hello-world': '/content/images/hello-world.jpg',
  'getting-started': '/content/images/getting-started.jpg',
  'advanced-features': '/content/images/advanced-features.jpg',
  'customization': '/content/images/customization.jpg',
  'deployment': '/content/images/deployment.jpg'
};

// 测试数据
const testPosts: Post[] = [
  {
    slug: 'hello-world',
    title: 'Hello World',
    date: '2024-03-20',
    author: 'John Doe',
    excerpt: 'Welcome to my blog! This is my first post...',
    content: 'Welcome to my blog! This is my first post...',
    htmlContent: '<p>Welcome to my blog! This is my first post...</p>',
    tags: ['introduction', 'welcome'],
    image: defaultImages['hello-world']
  },
  {
    slug: 'getting-started',
    title: 'Getting Started',
    date: '2024-03-21',
    author: 'John Doe',
    excerpt: 'Learn how to get started with our platform...',
    content: 'Learn how to get started with our platform...',
    htmlContent: '<p>Learn how to get started with our platform...</p>',
    tags: ['tutorial', 'beginner'],
    image: defaultImages['getting-started']
  }
];

// 缓存加载的文章
let postsCache: Post[] | null = null;

// 生成摘要
function generateExcerpt(content: string, maxLength: number = 200): string {
  // 移除 Markdown 语法
  const plainText = content.replace(/[#*`_~\[\]]/g, '');
  // 截取指定长度
  return plainText.length > maxLength ? plainText.slice(0, maxLength) + '...' : plainText;
}

// 获取所有文章
export async function getAllPosts(): Promise<Post[]> {
  try {
    // 1. 获取 list.json
    const listRes = await fetch('/blog_content/posts/list.json');
    if (!listRes.ok) {
      console.error('Failed to fetch list.json:', listRes.status, listRes.statusText);
      return [];
    }
    const files: string[] = await listRes.json();
    if (!Array.isArray(files) || files.length === 0) {
      console.warn('No posts found in list.json');
      return [];
    }

    // 2. 逐个 fetch md 文件
    const postPromises = files.map(async (filename) => {
      try {
        const res = await fetch(`/blog_content/posts/${filename}`);
        if (!res.ok) {
          console.error(`Failed to fetch ${filename}:`, res.status, res.statusText);
          return null;
        }
        const markdownContent = await res.text();
        const { attributes, body } = frontMatter<any>(markdownContent);
        const slug = filename.replace(/\.md$/, '');
        const htmlContent = await Promise.resolve(marked(body));
        const processedHtmlContent = processHtmlContent(htmlContent);
        const excerpt = generateExcerpt(body);
        // 兼容 image 和 coverImage 字段
        let imageUrl = (attributes.image || attributes.coverImage) ? processMediaPath(attributes.image || attributes.coverImage) : undefined;
        let video = attributes.video;
        if (video) {
          video = {
            ...video,
            url: video.type === 'local' ? processMediaPath(video.url) : video.url,
            thumbnail: video.thumbnail ? processMediaPath(video.thumbnail) : undefined
          };
        }
        let gallery = attributes.gallery;
        if (gallery?.images) {
          gallery = {
            ...gallery,
            images: gallery.images.map((img: string) => processMediaPath(img))
          };
        }
        return {
          slug,
          title: attributes.title || slug,
          date: attributes.date || new Date().toISOString(),
          author: attributes.author || 'Anonymous',
          excerpt,
          content: body,
          htmlContent: processedHtmlContent,
          tags: attributes.tags || [],
          image: imageUrl,
          video,
          gallery
        } as Post;
      } catch (err) {
        console.error('Error loading post', filename, err);
        return null;
      }
    });
    const loadedPosts = await Promise.all(postPromises);
    return loadedPosts.filter((p): p is Post => !!p).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    console.error('Error in getAllPosts:', error);
    return [];
  }
}

// 获取单个文章
export async function getPost(slug: string): Promise<Post | null> {
  const posts = await getAllPosts();
  return posts.find(post => post.slug === slug) || null;
}

// 获取所有标签
export async function getAllTags(): Promise<string[]> {
  const posts = await getAllPosts();
  const tags = new Set<string>();
  posts.forEach(post => {
    post.tags.forEach(tag => tags.add(tag));
  });
  return Array.from(tags).sort();
}

// 获取特定标签的文章
export async function getPostsByTag(tag: string): Promise<Post[]> {
  const posts = await getAllPosts();
  return posts.filter(post => post.tags.includes(tag));
}

// 清除缓存
export function clearPostsCache() {
  postsCache = null;
} 